import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { saveAs } from 'file-saver';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import Loader from '../Loader';
import formatDateTimeToPST from '../PSTDateConvert';

const ImportTNHistory = () => {
    const [name,     setName]     = useState('');
    const [token,    setToken]    = useState('');
    const [expire,   setExpire]   = useState('');
    const [role,     setRole]     = useState('');
    const [timeZone, setTimeZone] = useState('');
    const [access, setAccess] = useState([]);
    const [user,   setUser]   = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const fromCid  = location.state?.cid || '';

    const [history,      setHistory]      = useState([]);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyPage,  setHistoryPage]  = useState(1);
    const [pollingId,    setPollingId]    = useState(null);

    useEffect(() => { refreshToken(); getUserAccess(); }, []);
    useEffect(() => { if (role > 2) navigate('/dashboard'); }, [role]);
    useEffect(() => {
        if (user !== null && role !== '' && parseInt(role) > 1 && !user.hasEdit) navigate('/dashboard');
    }, [user, role]);
    useEffect(() => { if (token && user !== null) loadHistory(true); }, [token, user]);
    useEffect(() => { if (token && user !== null) loadHistory(true); }, [historyPage]);

    useEffect(() => {
        const hasProcessing = history.some(r => r.status === 1);
        if (hasProcessing && !pollingId) {
            const id = setInterval(() => loadHistory(false), 5000);
            setPollingId(id);
        } else if (!hasProcessing && pollingId) {
            clearInterval(pollingId);
            setPollingId(null);
        }
        return () => { if (pollingId) clearInterval(pollingId); };
    }, [history]);

    const axiosJWT = axios.create();
    axiosJWT.interceptors.request.use(async (config) => {
        if (expire * 1000 < Date.now()) {
            const res = await axios.get(`${baseUrl}/token`);
            config.headers.Authorization = `Bearer ${res.data.accessToken}`;
            setToken(res.data.accessToken);
            const decoded = jwt_decode(res.data.accessToken);
            setName(decoded.name); setExpire(decoded.exp);
        }
        return config;
    }, err => Promise.reject(err));

    const refreshToken = async () => {
        try {
            const res = await axios.get(`${baseUrl}/token`);
            setToken(res.data.accessToken);
            const decoded = jwt_decode(res.data.accessToken);
            setName(decoded.name); setRole(decoded.role); setExpire(decoded.exp); setTimeZone(decoded.timezone);
        } catch { navigate('/'); window.location.reload(); }
    };

    const getUserAccess = async () => {
        try {
            const res = await axiosJWT.get(`${baseUrl}/user-access`, { headers: { Authorization: `Bearer ${token}` } });
            setAccess(res.data.results);
            setUser({ hasEdit: !!res.data.enrms_registration_edit_switch });
        } catch { navigate('/dashboard'); }
    };

    const loadHistory = async (showLoader = true) => {
        try {
            if (showLoader) setPopupOpen(true);
            const res = await axiosJWT.get(
                `${baseUrl}/registration/setup/import/history?page=${historyPage}&limit=20`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setHistory(res.data.data);
            setHistoryTotal(res.data.totalItem);
        } catch (e) { console.log(e); }
        finally { if (showLoader) setPopupOpen(false); }
    };

    const downloadResult = async (id, row) => {
        try {
            setPopupOpen(true);
            const res = await axiosJWT.get(
                `${baseUrl}/registration/setup/import/history/${id}/result`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data.processing) {
                alert('Import is still processing. Please check back shortly.');
                setPopupOpen(false);
                return;
            }
            const data = res.data.data || {};
            const inserted = (data.inserted_values || []).map(v => `"${v}","Success"`);
            const errors   = (data.errors || []).map(e => `"${e.value}","${e.error}"`);
            const csv = `"Value","Status"\n${[...inserted, ...errors].join('\n')}`;
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const filename = `import_result_${row.import_type}_${String(row.created_at).slice(0,10)}.csv`;
            saveAs(blob, filename);
        } catch (e) { console.log(e); }
        setPopupOpen(false);
    };

    return (
        <div className="panelbox">
            <Navbar token={token} access={access} />
            <div className="right-panel">
                <Header name={name} />
                {(user === null || popupOpen) && <Loader />}
                <div className="content-page admin-attendance-page">
                    <div className="row">
                        <div className="col-6"><div className="left-title"><h1>Import TN / Tag History</h1></div></div>
                        <div className="col-6 inventory-btn">
                            <div className="addnew-button">
                                <Link to="/enrms-registration-import" state={{ cid: fromCid }}><span>&larr;</span> Back</Link>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12 common-white-shadow-background setting-section">
                            <div className="fullwidth-table track-table-body">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Customer</th>
                                            <th>Type</th>
                                            <th>Details</th>
                                            <th>Language</th>
                                            <th>Imported By</th>
                                            <th>Result</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.length === 0 && (
                                            <tr><td colSpan={7} style={{ textAlign:'center' }}><em>No import history.</em></td></tr>
                                        )}
                                        {history.map((row, i) => (
                                            <tr key={i}>
                                                <td style={{ textAlign:'left', paddingLeft:0 }}>{formatDateTimeToPST(row.created_at, timeZone, role)}</td>
                                                <td style={{ textAlign:'left', paddingLeft:0 }}>{row.cname}</td>
                                                <td style={{ textAlign:'left', paddingLeft:0 }}>{row.import_type?.toUpperCase()}</td>
                                                <td style={{ textAlign:'left', paddingLeft:0 }}>{row.registration_short_description || row.reg_details_id}</td>
                                                <td style={{ textAlign:'left', paddingLeft:0 }}>{row.lang_short_description || row.reg_lang_id}</td>
                                                <td style={{ textAlign:'left', paddingLeft:0 }}>{row.full_name}</td>
                                                <td style={{ textAlign:'left', paddingLeft:0 }}>
                                                    {row.status === 2
                                                        ? <button onClick={() => downloadResult(row.id, row)}><img src={`${process.env.PUBLIC_URL}/images/download.png`} alt="Download" /></button>
                                                        : row.status === 3
                                                            ? 'Failed'
                                                            : 'In-Progress'
                                                    }
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {historyTotal > 20 && (
                                    <div style={{ marginTop:'10px' }}>
                                        <button disabled={historyPage === 1} onClick={() => setHistoryPage(p => p-1)} className="btn btn-sm btn-secondary" style={{ marginRight:'8px' }}>Prev</button>
                                        <span>Page {historyPage}</span>
                                        <button disabled={historyPage * 20 >= historyTotal} onClick={() => setHistoryPage(p => p+1)} className="btn btn-sm btn-secondary" style={{ marginLeft:'8px' }}>Next</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImportTNHistory;
